import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Separator } from '../components/ui/separator';
import { LogoUpim } from '../components/ui-upim';
import {
  Mail,
  Lock,
  Eye,
  EyeOff,
  ArrowRight,
  Loader2
} from 'lucide-react';
import { toast } from '../hooks/use-toast';
import { authAPI } from '../services/api';
import { accessDestination } from '../services/accessDestination';
import TotpLoginForm from '../components/TotpLoginForm';

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  
  const { loginWithEmail, verifyTotpLogin } = useAuth();
  const [pendingToken, setPendingToken] = useState(null);
  const navigate = useNavigate();
  const [params] = useSearchParams();

  const redirectAfterLogin = async (user) => {
    const destination = await accessDestination(user);
    navigate(params.get('next') === 'affiliation' && !destination.startsWith('/cuenta/') ? '/afiliacion/solicitud'
      : destination === '/dashboard' && user.role === 'invitado' ? '/dashboard/invitado' : destination);
  };

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      const res = await loginWithEmail(email, password);
      if (res?.totpRequired) {
        setPendingToken(res.pendingToken);
        setPassword('');
        setIsLoading(false);
        return;
      }
      if (res?.mustChangePassword) {
        toast({ title: 'Cambio de contraseña obligatorio', description: 'Define tu contraseña definitiva para continuar.' });
        navigate('/cuenta/seguridad');
        return;
      }
      toast({
        title: '¡Bienvenido!',
        description: 'Has iniciado sesión correctamente.',
      });
      await redirectAfterLogin(res.user);
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message || 'No se pudo iniciar sesión.',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleTotp = async (pending, code, backup) => {
    setIsLoading(true);
    try {
      const res = await verifyTotpLogin(pending, code, backup);
      if (res?.mustChangePassword) {
        navigate('/cuenta/seguridad');
        return;
      }
      toast({ title: '¡Bienvenido!', description: 'Verificación en dos pasos correcta.' });
      await redirectAfterLogin(res.user);
    } catch (error) {
      toast({ title: 'Código incorrecto', description: error.message || 'Intenta de nuevo.', variant: 'destructive' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    setIsGoogleLoading(true);
    // Fuente canónica: el backend valida el redirect contra la allowlist del ambiente.
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const currentUrl = window.location.origin;
    try {
      const data = await authAPI.googleAuthorizeUrl(currentUrl);
      window.location.href = data.authorize_url;
    } catch (e) {
      toast({ title: 'No se pudo abrir Google', description: e.message || 'Intenta de nuevo.', variant: 'destructive' });
      setIsGoogleLoading(false);
    }
  };

  return (
    <div className="upim-halo relative flex min-h-screen items-center justify-center bg-black p-4" data-testid="login-pagina">
      <div className="upim-aparece relative w-full max-w-md">
        {/* Logo */}
        <div className="mb-10 flex justify-center">
          <Link to="/" className="inline-flex items-center" aria-label="Ir al inicio">
            <LogoUpim className="h-14" claseTexto="text-3xl" testId="login-logo" />
          </Link>
        </div>

        <Card className="border border-[var(--upim-borde)] bg-[var(--upim-superficie-2)] shadow-none">
          <CardHeader className="pb-2 text-left">
            <p className="mb-2 text-[10px] font-semibold uppercase tracking-[0.26em] text-[var(--upim-acento)]">
              Acceso de socios
            </p>
            <CardTitle className="font-display text-2xl font-extrabold text-[var(--upim-texto)]">Iniciar Sesión</CardTitle>
            <CardDescription className="text-[var(--upim-texto-2)]">
              Accede a tu cuenta de afiliado UPIM
            </CardDescription>
          </CardHeader>
          <CardContent className="pt-6">
            {/* Google Login */}
            <Button
              variant="outline"
              className="mb-4 h-12 w-full border-[var(--upim-borde-fuerte)] bg-transparent text-[var(--upim-texto)] hover:border-[var(--upim-acento)] hover:bg-transparent hover:text-[var(--upim-acento)]"
              onClick={handleGoogleLogin}
              disabled={isGoogleLoading}
              data-testid="google-login-btn"
            >
              {isGoogleLoading ? (
                <Loader2 className="h-5 w-5 animate-spin" />
              ) : (
                <>
                  <svg className="h-5 w-5 mr-2" viewBox="0 0 24 24">
                    <path
                      fill="currentColor"
                      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                    />
                    <path
                      fill="currentColor"
                      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                    />
                    <path
                      fill="currentColor"
                      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                    />
                    <path
                      fill="currentColor"
                      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                    />
                  </svg>
                  Continuar con Google
                </>
              )}
            </Button>

            <div className="relative my-6">
              <Separator />
              <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-[var(--upim-superficie-2)] px-3 text-xs uppercase tracking-[0.2em] text-[var(--upim-texto-3)]">
                o
              </span>
            </div>

            {/* Email Login Form */}
            {pendingToken ? (
              <TotpLoginForm pendingToken={pendingToken} onVerify={handleTotp} isLoading={isLoading} onCancel={() => setPendingToken(null)} />
            ) : (
            <form onSubmit={handleEmailLogin} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Correo electrónico</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="tu@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-10 h-12"
                    required
                    data-testid="login-email-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="password">Contraseña</Label>
                  <Link
                    to="/recuperar-password"
                    className="text-sm text-gray-600 hover:text-gray-800"
                  >
                    ¿Olvidaste tu contraseña?
                  </Link>
                </div>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pl-10 pr-10 h-12"
                    required
                    data-testid="login-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
              </div>

              <Button
                type="submit"
                className="h-12 w-full rounded-full bg-[var(--upim-acento)] font-semibold text-black hover:bg-[var(--upim-acento-2)]"
                disabled={isLoading}
                data-testid="login-submit-btn"
              >
                {isLoading ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : (
                  <>
                    Iniciar Sesión
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </>
                )}
              </Button>
            </form>
            )}


            <p className="mt-6 text-center text-sm text-[var(--upim-texto-2)]">
              ¿No tienes cuenta?{' '}
              <Link to="/registro" className="font-medium text-[var(--upim-acento)] hover:text-[var(--upim-acento-2)]">
                Regístrate aquí
              </Link>
            </p>
            
          </CardContent>
        </Card>

        <p className="text-center text-sm text-gray-500 mt-6">
          <Link to="/" className="hover:text-gray-700">
            ← Volver al inicio
          </Link>
        </p>
      </div>
    </div>
  );
};

export default LoginPage;
